'use client'

import Image from 'next/image'
import { useParams } from 'next/navigation'
import { Image as DatoImage } from 'react-datocms'
import { formatDate } from '@/utils/date'
import { cn } from '@desoutter/uikit'
import { ArticleRecord } from '@/graphql/types'
import { Button, Calendar, Heading, RightTopArrow } from '@desoutter/uikit'
import { LocalizedLink } from '@/components/LocalizedLink'

type ArticlePreviewProps = {
	data: ArticleRecord
	hideDate?: boolean
	isLocked?: boolean
}

export function ArticlePreview({
	data,
	hideDate = false,
	isLocked = false,
}: ArticlePreviewProps) {
	const { lang } = useParams()

	const fallbackImage = data.thumbnailImage?.responsiveImage
		? data.thumbnailImage
		: data.heroImage?.responsiveImage
			? data.heroImage
			: null
	return (
		<LocalizedLink
			locale={lang}
			prefetch={false}
			href={`/articles/${data.slug}`}
			className={cn(
				'relative flex flex-col mb-5 gap-5 w-full',
				data.span === 2 && 'col-span-2',
				data.span === 3 && 'col-span-3',
				data.backgroundImage &&
					'justify-end h-[587px] bg-cover rounded-2xl text-foreground',
			)}
		>
			{data.backgroundImage && (
				<DatoImage
					layout="fill"
					objectFit="cover"
					data={data.backgroundImage.responsiveImage}
					pictureClassName="!transition-all !duration-500 hover:scale-110"
					className="absolute h-full rounded-2xl"
				/>
			)}
			<div className="absolute top-4 left-4 flex flex-row flex-wrap gap-1 z-20">
				{data.industries.map((industry) => (
					<div
						key={industry.id}
						className="bg-foreground text-background rounded px-3 py-1"
					>
						<p className="text-xs">{industry.label}</p>
					</div>
				))}
				{isLocked && (
					<div className="bg-foreground rounded px-3 pt-1.5">
						<Image src="/images/lock.svg" width={12} height={12} alt="lock" />
					</div>
				)}
			</div>

			{!data.backgroundImage && fallbackImage?.responsiveImage && (
				<DatoImage
					data={fallbackImage.responsiveImage}
					objectFit="cover"
					layout="responsive"
					objectPosition={`${fallbackImage.focalPoint.x * 100}% ${
						fallbackImage.focalPoint.y * 100
					}%`}
					pictureClassName="!transition-all !duration-500 hover:scale-110"
					className="h-[271px] rounded-2xl"
				/>
			)}

			<div className="absolute top-4 right-4 z-20">
				<Button
					size="xl"
					title="go to article"
					color="tertiary"
					icon={RightTopArrow}
				/>
			</div>

			<div
				className={cn(
					'relative flex flex-col justify-between max-w-[800px] min-h-36',
					data.backgroundImage && 'p-6',
				)}
			>
				<div className="z-20">
					<Heading
						as="h3"
						className={cn(
							'h3 tracking-normal font-medium leading-tight dark:text-foreground text-inherit',
							data.backgroundImage
								? 'text-2xl leading-tight text-white'
								: 'text-lg',
						)}
					>
						{data.title}
					</Heading>

					<p
						className={cn(
							'text-sm line-clamp-2 dark:text-foreground text-inherit',
							data.backgroundImage && 'text-white',
						)}
					>
						{data.description}
					</p>
				</div>
				{!hideDate && data.altDate && data.displayDate && (
					<div className="flex items-center mt-3 gap-2 z-20">
						<Calendar
							variant={
								data.backgroundImage ? 'stroke-white' : 'stroke-gray-600'
							}
						/>
						<p
							className={cn(
								'text-sm',
								!data.backgroundImage ? 'text-gray-600' : 'text-white',
							)}
						>
							{formatDate(data.altDate)}
						</p>
					</div>
				)}
			</div>
		</LocalizedLink>
	)
}
